import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X, BadgeCheck, Sparkles } from "lucide-react";
import { fetchLawyers } from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { toFa } from "@/lib/fa";

export default function Lawyers() {
  const { data, isLoading } = useQuery({ queryKey: ["lawyers"], queryFn: fetchLawyers });
  const [selected, setSelected] = useState(null);
  const lawyers = data?.data ?? [];
  const lead = lawyers.find((l) => l.slug === "dr-mohammad-ahmadi");
  const rest = lawyers.filter((l) => l.slug !== "dr-mohammad-ahmadi");

  return (
    <>
      <section className="relative overflow-hidden pb-10 pt-36" data-testid="lawyers-page-hero">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 0%, rgba(212,175,55,0.1), transparent)" }} />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading as="h1" eyebrow="تیم وکلا و مشاوران" title="وکلای رسمی، <span class='gold-text'>ثبت‌شده و بین‌المللی</span>" desc="تیم گروه ۷ به رهبری دکتر محمد احمدی؛ وکیل و مشاور رسمی مهاجرت در آمریکا، کانادا، استرالیا و اروپا با کارنامه بیش از ۲۵۰۰ پرونده موفق." />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        {isLoading ? (
          <div className="h-96 animate-pulse rounded-3xl bg-navy-850/50" />
        ) : (
          lead && (
            <Reveal>
              <div className="card-glow grid overflow-hidden rounded-3xl border border-gold/25 bg-navy-850 md:grid-cols-[0.85fr_1.15fr]" data-testid="lead-lawyer-card">
                <div className="relative min-h-72 overflow-hidden">
                  <img src={lead.image} alt={lead.name} className="img-treatment absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "50% 28%" }} />
                  <div className="absolute inset-0 bg-gradient-to-l from-navy-850/60 to-transparent" />
                  <span className="absolute right-5 top-5 flex items-center gap-1.5 rounded-full bg-gold px-4 py-1.5 text-xs font-black text-navy-950"><Sparkles size={13} />مدیر و بنیان‌گذار</span>
                </div>
                <div className="p-8 sm:p-10">
                  <h2 className="font-display text-3xl font-black text-slate-50">{lead.name}</h2>
                  <p className="mt-1 text-sm font-bold text-gold">{lead.role} — {toFa(lead.experience)}+ سال سابقه</p>
                  <p className="mt-5 text-sm leading-8 text-slate-300">{lead.bio}</p>
                  <p className="mt-3 text-sm leading-8 text-slate-400">{lead.bio2}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {lead.licenses.map((l) => (
                      <span key={l} className="flex items-center gap-1.5 rounded-full border border-gold/30 px-3 py-1.5 text-[11px] font-semibold text-gold"><BadgeCheck size={12} />{l}</span>
                    ))}
                  </div>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/contact" className="btn-gold" data-testid="lead-lawyer-consult-cta">رزرو مشاوره با دکتر احمدی</Link>
                    <button onClick={() => setSelected(lead)} className="btn-ghost" data-testid="lead-lawyer-profile-button">پروفایل کامل</button>
                  </div>
                </div>
              </div>
            </Reveal>
          )
        )}

        <Stagger className="mt-10 grid gap-6 md:grid-cols-3">
          {rest.map((l) => (
            <StaggerItem key={l.slug}>
              <div className="card-glow group h-full overflow-hidden rounded-3xl border border-gold/15 bg-navy-850" data-testid={`lawyers-page-card-${l.slug}`}>
                <div className="relative h-72 overflow-hidden">
                  <img src={l.image} alt={l.name} className="img-treatment h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-850 via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <h4 className="font-display text-lg font-extrabold text-slate-50">{l.name}</h4>
                  <p className="mt-1 text-sm text-gold/90">{l.role}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {l.specialties.slice(0, 2).map((sp) => <span key={sp} className="rounded-full bg-navy-700 px-3 py-1 text-[11px] text-slate-300">{sp}</span>)}
                  </div>
                  <button onClick={() => setSelected(l)} className="mt-5 flex items-center gap-2 text-sm font-bold text-gold transition-all hover:gap-3" data-testid={`lawyers-page-profile-button-${l.slug}`}>
                    مشاهده پروفایل<ArrowLeft size={15} />
                  </button>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mt-16">
          <div className="glass-panel flex flex-col items-center gap-3 rounded-3xl border border-gold/25 p-10 text-center">
            <p className="font-display text-xl font-extrabold sm:text-2xl">بیش از ۳۵ وکیل و کارشناس <span className="gold-text">آماده پرونده شما</span></p>
            <p className="max-w-lg text-sm leading-7 text-slate-400">هر پرونده در گروه ۷ یک تیم سه‌نفره اختصاصی دارد: وکیل ارشد، مدیر پرونده و کارشناس مدارک.</p>
            <Link to="/contact" className="btn-gold mt-2" data-testid="lawyers-team-consult-cta">تشکیل تیم پرونده من<ArrowLeft size={16} /></Link>
          </div>
        </Reveal>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelected(null)} className="fixed inset-0 z-[70] flex items-center justify-center bg-navy-950/85 p-4 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.92, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} className="glass-panel max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-gold/30" data-testid="lawyers-page-modal">
              <img src={selected.image} alt={selected.name} className="img-treatment h-56 w-full rounded-t-3xl object-cover object-top" />
              <div className="p-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-extrabold text-gold">{selected.name}</h3>
                    <p className="mt-1 text-sm text-slate-400">{selected.role}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/25 text-gold" data-testid="lawyers-page-modal-close"><X size={16} /></button>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {selected.licenses.map((l) => <span key={l} className="rounded-full bg-gold/10 px-3 py-1 text-[11px] font-semibold text-gold">{l}</span>)}
                </div>
                <p className="mt-5 text-sm leading-8 text-slate-300">{selected.bio}</p>
                {selected.bio2 && <p className="mt-3 text-sm leading-8 text-slate-400">{selected.bio2}</p>}
                <div className="mt-5 flex flex-wrap gap-2">
                  {selected.specialties.map((sp) => <span key={sp} className="rounded-full bg-navy-700 px-3 py-1 text-[11px] text-slate-300">{sp}</span>)}
                </div>
                <Link to="/contact" className="btn-gold mt-6 w-full" data-testid="lawyers-page-modal-book">رزرو مشاوره با {selected.name}</Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
